/**
 * Configuración remota de la app.
 *
 * Permite encender y apagar elementos de la interfaz sin recompilar ni pasar
 * por las tiendas: basta con editar `public/app-config.json` y desplegar la web.
 *
 * No descarga código, solo valores. Lo que se muestra u oculta ya viaja dentro
 * del binario que revisan las tiendas.
 *
 * Estrategia, la misma que `monthlyMottos.js`:
 *  - Lectura síncrona del cache al arrancar, para que la interfaz no parpadee.
 *  - Petición a la red en segundo plano, que actualiza el cache.
 *  - Si no hay red ni cache, valores por defecto compilados.
 */

import { APP_DOMAIN } from '../config';

const CONFIG_URL = `${APP_DOMAIN}/app-config.json`;
const CACHE_KEY = 'agustinos_remote_config';
const CACHE_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 días

// Valores por defecto. Solo se usan en una instalación nueva sin conexión,
// antes de que la app haya podido consultar el fichero ni una vez.
const DEFAULTS = {
  mostrarCaminoHome: true
};

const leerCache = () => {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const cached = JSON.parse(raw);
    if (!cached || (Date.now() - cached.at) >= CACHE_TTL_MS) return null;
    return cached.values || null;
  } catch {
    return null;
  }
};

/**
 * Configuración disponible de inmediato, sin esperar a la red.
 * Sirve como estado inicial para que la portada no cambie a media carga.
 */
export const getConfigInicial = () => ({ ...DEFAULTS, ...(leerCache() || {}) });

/**
 * Consulta el fichero y actualiza el cache. Nunca lanza: si falla, devuelve
 * lo que hubiera disponible.
 */
export const fetchRemoteConfig = async () => {
  try {
    const res = await fetch(CONFIG_URL, {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    });
    if (res.ok) {
      const body = await res.json();
      // Solo se aceptan las claves conocidas, para que un fichero mal editado
      // no meta valores inesperados en la interfaz.
      const values = {};
      Object.keys(DEFAULTS).forEach(k => {
        if (typeof body?.[k] === typeof DEFAULTS[k]) values[k] = body[k];
      });

      try {
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem(CACHE_KEY, JSON.stringify({ values, at: Date.now() }));
        }
      } catch { /* ignore */ }

      return { ...DEFAULTS, ...values };
    }
  } catch {
    // Sin red: se mantiene lo que ya hubiera.
  }

  return getConfigInicial();
};
